import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { track, Events } from '../lib/analytics'

export default function Rate() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const [myRating, setMyRating] = useState(0)
  const [allRatings, setAllRatings] = useState([])
  const [saving, setSaving] = useState(false)

  const watch = location.state?.watch
  if (!watch) { navigate('/history'); return null }

  const movie = watch.movies

  useEffect(() => {
    loadRatings()
  }, [])

  async function loadRatings() {
    const { data } = await supabase
      .from('ratings')
      .select('*, profiles!ratings_user_id_fkey(display_name)')
      .eq('watch_history_id', watch.id)

    setAllRatings(data || [])

    const mine = data?.find(r => r.user_id === user.id)
    if (mine) setMyRating(mine.score)
  }

  async function submitRating(score) {
    setMyRating(score)
    setSaving(true)
    try {
      // Upsert rating
      const { data: existing } = await supabase
        .from('ratings')
        .select('id')
        .eq('watch_history_id', watch.id)
        .eq('user_id', user.id)
        .single()

      if (existing) {
        await supabase.from('ratings').update({ score }).eq('id', existing.id)
      } else {
        await supabase.from('ratings').insert({
          watch_history_id: watch.id,
          user_id: user.id,
          score,
        })
      }
      track(Events.RatingSubmitted, { score })
      await loadRatings()
    } catch (err) {
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  const colors = ['#e94560', '#0f3460', '#533483', '#c73e54', '#1a3a6b']

  return (
    <div className="pb-24 px-4 pt-4 max-w-lg mx-auto">
      <button
        onClick={() => navigate('/history')}
        className="text-sm text-[var(--fp-red)] bg-transparent border-none cursor-pointer mb-4"
      >
        ← Back
      </button>

      <div className="text-center">
        <button
          onClick={() => movie?.tmdb_id && navigate(`/movie/${movie.tmdb_id}`)}
          className="w-36 h-[210px] rounded-xl overflow-hidden mx-auto mb-4 bg-[var(--fp-border)] cursor-pointer border-none p-0 block"
        >
          {movie?.poster_url ? (
            <img src={movie.poster_url} alt={movie.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-5xl">🎬</div>
          )}
        </button>
        <button
          onClick={() => movie?.tmdb_id && navigate(`/movie/${movie.tmdb_id}`)}
          className="text-xl font-bold text-white bg-transparent border-none cursor-pointer p-0 hover:text-[var(--fp-red)] transition-colors"
          style={{ fontFamily: 'var(--fp-font-display)', letterSpacing: '-0.03em' }}
        >
          {movie?.title}
        </button>
        <p className="text-[13px] text-[var(--fp-text-muted)] mt-1">
          {[movie?.year, movie?.genre, movie?.runtime_minutes && `${Math.floor(movie.runtime_minutes / 60)}h ${movie.runtime_minutes % 60}m`].filter(Boolean).join(' · ')}
        </p>
        <p className="text-xs text-[var(--fp-text-muted)] mt-1">
          Watched {watch.watched_on && new Date(watch.watched_on + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
          {watch.profiles?.display_name && ` · Suggested by ${watch.profiles.display_name}`}
        </p>
        <button
          onClick={() => movie?.tmdb_id && navigate(`/movie/${movie.tmdb_id}`)}
          className="text-xs text-[var(--fp-red)] bg-transparent border-none cursor-pointer mt-2 font-semibold"
        >
          View trailer, cast & more →
        </button>
      </div>

      {/* Rating Input */}
      <div className="bg-[var(--fp-card)] rounded-2xl p-6 text-center mt-6 border border-[var(--fp-border-accent)]">
        <h3 className="text-base font-bold text-white mb-1">Your Rating</h3>
        <p className="text-[13px] text-[var(--fp-text-muted)] mb-4">How was it?</p>
        <div className="flex justify-center gap-2 mb-4">
          {[1, 2, 3, 4, 5].map(n => (
            <button
              key={n}
              onClick={() => submitRating(n)}
              disabled={saving}
              className="w-11 h-11 rounded-full flex items-center justify-center text-xl cursor-pointer border-2 transition-all"
              style={{
                background: n <= myRating ? 'var(--fp-red)' : 'var(--fp-surface)',
                borderColor: n <= myRating ? 'var(--fp-red)' : 'var(--fp-border)',
              }}
            >
              ★
            </button>
          ))}
        </div>
      </div>

      {/* Group Ratings */}
      {allRatings.length > 0 && (
        <div className="mt-6">
          <h3 className="text-base font-bold text-white mb-3">Group Ratings</h3>
          {allRatings.map((r, i) => (
            <div key={r.id} className="flex items-center gap-3 py-2.5 border-b border-[var(--fp-surface)]">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold text-white"
                style={{ background: colors[i % colors.length] }}
              >
                {r.profiles?.display_name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <span className="flex-1 text-sm text-white">{r.profiles?.display_name || 'Unknown'}</span>
              <span className="text-[var(--fp-red)] text-sm">
                {'★'.repeat(r.score)}
                <span className="text-[var(--fp-border)]">{'★'.repeat(5 - r.score)}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Next action */}
      {myRating > 0 && (
        <div className="mt-8 flex flex-col gap-3">
          <button
            onClick={() => navigate('/')}
            className="fp-btn-primary w-full py-3"
          >
            Back to Suggestion Pool
          </button>
          <button
            onClick={() => navigate('/history')}
            className="fp-btn-secondary w-full py-3"
          >
            View Watch History
          </button>
        </div>
      )}
    </div>
  )
}
